<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="4" />
      <vs-col
        vs-w="7"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <date-picker
              type="date"
              format="DD MMM YYYY"
              value-type="YYYY-MM-DD"
              placeholder="Start Date"
              v-model="filters.startDate"
              :min="minDate"
              :max="maxDate"
            />
          </div>
          <div class="flex items-center w-full">
            <date-picker
              type="date"
              format="DD MMM YYYY"
              value-type="YYYY-MM-DD"
              placeholder="End Date"
              v-model="filters.endDate"
              :min="filters.startDate || minDate"
              :max="filters.startDate ? moment(filters.startDate).add(1, 'year').format('YYYY-MM-DD') : maxDate"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px;"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5"
        @fetch="fetchCleanlinessInspections"
        ref="tabletop"
      >
        <template #start>
          <vs-button
            v-show="true"
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
            :disabled="isDownload"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :loading="isBusy"
        :table-header="tableHeaders"
        :table-data="cleanlinessData"
        :sort-by="filters.sortBy"
        :sort-dir="filters.sortDir"
        body-height="100vh"
        @sort="sort"
      >
        <template #no="{ row }">
          {{ row.no }}
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <div
              :class="`flex items-center justify-center icon-transparent-bg ${isProcessing && selectedCleanlinessId === row.id && 'cursor-not-allowed opacity-50 animate-bounce'}`"
              @click="isProcessing && selectedCleanlinessId === row.id ? null : openCleanlinessDetail(row.id)"
            >
              <img
                :src="require('@/assets/shipo-icon-see.svg')"
                class="w-8 h-8"
              >
            </div>
            <div
              :class="`flex items-center justify-center icon-transparent-bg ${isDownloadDetails && selectedCleanlinessId === row.id && 'opacity-50 cursor-not-allowed animate-bounce'}`"
              @click="isDownloadDetails && selectedCleanlinessId === row.id ? null : handleExportDetails(row.id)"
            >
              <img
                :src="require('@/assets/shipo-icon-file-download-primary.svg')"
                class="w-5 h-5"
              >
            </div>
          </div>
        </template>
        <template #inspection_date="{ row }">
          {{ row.inspection_date }}
        </template>
        <template #location="{ row }">
          {{ row.location }}
        </template>
        <template #tug_name="{ row }">
          {{ row.tug_name }}
        </template>
        <template #barge_name="{ row }">
          {{ row.barge_name }}
        </template>
        <template #result="{ row }">
          <span
            :class="[
              row.result ? 'green-bg' : 'red-bg',
              'block w-full text-center py-2 rounded font-medium'
            ]"
          >
            {{ row.result ? 'Clean' : 'Dirty' }}
          </span>
        </template>
        <template #contamination_found="{ row }">
          <span
            :class="[
              row.contamination_found ? 'red-bg' : 'green-bg',
              'block w-full text-center py-2 rounded font-medium'
            ]"
          >
            {{ row.contamination_found ? 'Yes' : 'No' }}
          </span>
        </template>
      </data-table>
      <table-bottom @fetch="fetchCleanlinessInspections" />
    </div>
    <pop-up
      :active.sync="showCleanlinessDetail"
      title="Barge Hold/Hatch Cleanliness"
      :hide-illustration="true"
      :hide-ok-button="true"
      @close="showCleanlinessDetail = false"
    >
      <div
        class="p-4"
        style="width: 600px; max-width: 100vw;"
        v-if="cleanlinessDetailData && selectedCleanlinessId === cleanlinessDetailData.id"
      >
        <div class="mb-4">
          <span class="font-semibold text-orange-600 text-xl">General Information</span>
          <div class="grid grid-cols-2 gap-2 gap-y-4 mt-4 text-black">
            <div>
              <div><b>Inspection Date</b></div>
              <div class="mt-2">
                {{ formatDate(cleanlinessDetailData.inspection_date) }}
              </div>
            </div>
            <div>
              <div><b>Tug Owner</b></div>
              <div class="mt-2">
                {{ cleanlinessDetailData.tug_owner }}
              </div>
            </div>
            <div>
              <div><b>Location</b></div>
              <div class="mt-2">
                {{ cleanlinessDetailData.location || cleanlinessDetailData.locaton }}
              </div>
            </div>
            <div>
              <div><b>Barge Name</b></div>
              <div class="mt-2">
                {{ cleanlinessDetailData.barge_name }}
              </div>
            </div>
          </div>
        </div>
        <div class="font-semibold text-orange-600 text-xl mb-2">
          Barge
        </div>
        <div
          v-for="(deck) in cleanlinessDetailData.decks"
          :key="deck.id"
          class="mb-6 mt-4"
        >
          <div
            :class="[
              'font-semibold',
              'mb-4',
              'cursor-pointer',
              'text-base',
              'text-purple-700'
            ]"
            style="width: fit-content"
          >
            Deck Space {{ deck.part.charAt(0) + deck.part.slice(1).toLowerCase().replace('_', ' ') }} Part
          </div>
          <div
            v-if="deck.pictures && deck.pictures.length"
            class="mb-4"
          >
            <div class="font-semibold mb-2 text-black">
              Picture
            </div>
            <div class="flex gap-2">
              <img
                v-for="(pic, idx) in deck.pictures"
                :key="pic.id || idx"
                :src="pic.download_url"
                class="w-24 h-20 object-cover rounded cursor-pointer"
                @click="openImageCarousel(deck.pictures, idx, deck.part)"
              >
            </div>
          </div>
          <div class="mt-2">
            <div class="mb-4">
              <div class="text-black font-semibold">
                Location Status
              </div>
              <div :class="deck.location_status === 'Clean' ? 'text-green-600' : 'text-red-600'">
                <span class="font-semibold flex items-center">
                  <img
                    :src="deck.location_status === 'Clean' ? require('@/assets/shipo-icon-checkmark.svg') : require('@/assets/shipo-icon-close.svg')"
                    class="w-10 h-10 pt-1"
                  >
                  {{ deck.location_status }}
                </span>
              </div>
            </div>
            <div class="mb-4">
              <div class="text-black font-semibold">
                Contamination Found
              </div>
              <div :class="deck.contamination_found === 'No' ? 'text-green-600' : 'text-red-600'">
                <span class="font-semibold flex items-center">
                  <img
                    :src="deck.contamination_found === 'No' ? require('@/assets/shipo-icon-checkmark.svg') : require('@/assets/shipo-icon-close.svg')"
                    class="w-10 h-10 pt-1"
                  >
                  {{ deck.contamination_found }}
                </span>
              </div>
            </div>
            <div class="mb-4">
              <div class="text-black font-semibold">
                Remarks
              </div>
              <div class="mt-2 text-black">
                {{ deck.remarks }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <vs-button
        color="primary"
        :class="`w-full font-semibold mt-4 ${isDownloadDetails && 'opacity-50 cursor-not-allowed animate-bounce'}`"
        @click="isDownloadDetails = true"
      >
        Export Data
      </vs-button>
    </pop-up>
    <pop-up
      :active.sync="showImageCarousel"
      :hide-illustration="true"
      :z-index="10000"
      class="custom-carousel-popup"
      :title="''"
      :hide-ok-button="true"
      @close="showImageCarousel = false"
    >
      <div class="carousel-header flex justify-between items-center mb-4">
        <div>
          <span class="font-bold text-xl text-black">Picture - </span>
          <span class="text-purple-600 text-xl font-semibold">
            Deck Space {{ carouselDeckName }} Part
          </span>
        </div>
      </div>
      <div class="flex flex-col items-center">
        <div class="relative mb-4">
          <img
            :src="carouselImages[carouselIndex]"
            class="main-carousel-img"
          >
          <button
            v-if="carouselImages.length > 1"
            class="carousel-arrow left"
            @click="carouselIndex = (carouselIndex - 1 + carouselImages.length) % carouselImages.length"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            ><circle
              cx="20"
              cy="20"
              r="20"
              fill="#fff"
              fill-opacity="0.7"
            /><path
              d="M23 27L17 20L23 13"
              stroke="#FF7F57"
              stroke-width="2"
              stroke-linecap="round"
            /></svg>
          </button>
          <button
            v-if="carouselImages.length > 1"
            class="carousel-arrow right"
            @click="carouselIndex = (carouselIndex + 1) % carouselImages.length"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            ><circle
              cx="20"
              cy="20"
              r="20"
              fill="#fff"
              fill-opacity="0.7"
            /><path
              d="M17 13L23 20L17 27"
              stroke="#FF7F57"
              stroke-width="2"
              stroke-linecap="round"
            /></svg>
          </button>
        </div>
        <div class="flex gap-4 mt-2">
          <div
            v-for="(img, idx) in carouselImages"
            :key="idx"
            class="carousel-thumb-wrapper"
            :class="{ selected: carouselIndex === idx }"
            @click="carouselIndex = idx"
          >
            <img
              :src="img"
              class="carousel-thumb"
            >
          </div>
        </div>
      </div>
    </pop-up>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import PopUp from '@/components/popUp'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  components: {
    DataTable,
    TableBottom,
    TableTop,
    DatePicker,
    PopUp
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    return {
      isBusy: false,
      isDownload: false,
      isDownloadDetails: false,
      isProcessing: false,
      filters: {
        sortBy: 'inspection_date',
        sortDir: 'desc',
        startDate: '',
        endDate: ''
      },
      minDate: '',
      maxDate: '',
      tableHeaders: [
        { key: 'no', label: 'No', width: '50px' },
        { key: 'action', label: 'Action', width: '50px' },
        { key: 'inspection_date', label: 'Inspection Date', sortable: true },
        { key: 'location', label: 'Location', sortable: true },
        { key: 'tug_name', label: 'Tug Name', sortable: true },
        { key: 'barge_name', label: 'Barge Name', sortable: true },
        { key: 'result', label: 'Result', sortable: true },
        { key: 'contamination_found', label: 'Contamination Founded', sortable: true }
      ],
      cleanlinessData: [],
      cleanlinessDetailData: {},
      showCleanlinessDetail: false,
      selectedCleanlinessId: null,
      showImageCarousel: false,
      carouselImages: [],
      carouselIndex: 0,
      carouselDeckName: ''
    }
  },
  async mounted() {
    await this.fetchCleanlinessInspections()
    if (this.selectedCleanlinessId) {
      await this.fetchCleanlinessInspectionDetails({ id: this.selectedCleanlinessId })
    }
  },
  computed: {
    ...mapGetters({
      totalPage: 'Table/totalPage',
      totalData: 'Table/totalData',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      cleanlinessInspections: 'HoldHatchCleanliness/cleanlinessInspections',
      cleanlinessInspectionDetails: 'HoldHatchCleanliness/cleanlinessInspectionDetails',
      cleanlinessInspectionFile: 'HoldHatchCleanliness/cleanlinessInspectionFile',
      cleanlinessInspectionDetailsFile: 'HoldHatchCleanliness/cleanlinessInspectionDetailsFile'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    cleanlinessInspections: {
      handler(val) {
        if (val && val.length > 0) {
          this.cleanlinessData = val.map((item, index) => ({
            no: (this.currentPage - 1) * this.pageSize + index + 1,
            id: item.id,
            inspection_date: this.formatDate(item.inspection_date),
            location: item.locations?.name || '-',
            tug_name: item.tugs?.name || '-',
            barge_name: item.barges?.name || '-',
            result: !!item.result,
            contamination_found: !!item.contamination_found
          }))
        } else {
          this.cleanlinessData = []
        }
      }
    },
    cleanlinessInspectionDetails: {
      handler(val) {
        if (val) {
          this.cleanlinessDetailData = val;
        }
      }
    },
    'filters.startDate'(val) {
      if (this.filters.endDate && this.filters.endDate < val) {
        this.filters.endDate = '';
      }
      if (
        this.filters.endDate &&
        moment(this.filters.endDate).isAfter(moment(val).add(1, 'year'))
      ) {
        this.filters.endDate = '';
      }
      if (!val) {
        this.fetchCleanlinessInspections();
      }
    },
    'filters.endDate'(val) {
      if (val && this.filters.startDate) {
        this.fetchCleanlinessInspections();
      }
      if (!val) {
        this.fetchCleanlinessInspections();
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadCleanlinessInspection()
        }
      }
    },
    cleanlinessInspectionFile: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Hold-Hatch-Cleanliness-${this.filters.startDate}-${this.filters.endDate}.xlsx`)
          this.$store.dispatch('HoldHatchCleanliness/resetCleanlinessInspectionFile');
        }
      }
    },
    isDownloadDetails: {
      handler(val) {
        if (!!val) {
          this.downloadCleanlinessInspectionDetails()
        }
      }
    },
    cleanlinessInspectionDetailsFile: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Hold-Hatch-Cleanliness-Details-${this.selectedCleanlinessId}.xlsx`)
          this.$store.dispatch('HoldHatchCleanliness/resetCleanlinessInspectionDetailsFile');
        }
      }
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('DD MMM YYYY HH:mm')
    },
    async fetchCleanlinessInspections() {
      try {
        this.isBusy = true
        const params = {
          sort_dir: this.filters?.sortDir || 'asc',
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        }

        if (this.filters.startDate) {
          params.start_date = this.filters.startDate
        }

        if (this.filters.endDate) {
          params.end_date = this.filters.endDate
        }

        if (this.filters.sortBy) {
          params.sort_by = this.filters.sortBy
        }

        await this.$store.dispatch('HoldHatchCleanliness/fetchCleanlinessInspections', params)
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch cleanliness inspections, please try again')
      }
    },
    async fetchCleanlinessInspectionDetails({ id }) {
      try {
        await this.$store.dispatch('HoldHatchCleanliness/fetchCleanlinessInspectionDetails', id);
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch cleanliness inspection details, please try again');
      }
    },
    sort(sortBy, sortDir) {
      this.filters = {
        ...this.filters,
        sortBy: sortBy,
        sortDir: sortDir
      }
      this.fetchCleanlinessInspections()
    },
    async openCleanlinessDetail(id) {
      this.selectedCleanlinessId = id;
      this.isProcessing = true;
      await this.fetchCleanlinessInspectionDetails({ id });
      this.isProcessing = false;
      this.showCleanlinessDetail = true;
    },
    openImageCarousel(pictures, idx, part) {
      this.carouselImages = pictures.map(pic => pic.download_url);
      this.carouselIndex = idx;
      this.showImageCarousel = true;
      this.carouselDeckName = part.charAt(0) + part.slice(1).toLowerCase().replace('_', ' ');
    },
    exportCleanlinessDetailData() {
      alert('Export Data clicked!');
    },
    async downloadCleanlinessInspection() {
      try {
        this.isBusy = true
        await this.$store.dispatch('HoldHatchCleanliness/downloadCleanlinessInspection', {
          start_date: this.filters.startDate,
          end_date: this.filters.endDate
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false;
      } catch (error) {
        this.isDownload = false
        this.isBusy = false;
        this.showFailedNotification(this, 'Failed to download Hold Hatch Cleanliness, please try again')
      }
    },
    async downloadCleanlinessInspectionDetails() {
      try {
        await this.$store.dispatch('HoldHatchCleanliness/downloadCleanlinessInspectionDetails', this.selectedCleanlinessId, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isDownloadDetails = false;
      } catch (error) {
        this.isDownloadDetails = false
        this.showFailedNotification(this, 'Failed to download Hold Hatch Cleanliness Details, please try again')
      }
    },
    handleExportDetails(id) {
      this.selectedCleanlinessId = id;
      this.isDownloadDetails = true;
    },
    resetFilters() {
      this.filters = {
        sortBy: 'inspection_date',
        sortDir: 'desc',
        startDate: '',
        endDate: ''
      }
      this.$refs.tabletop.$emit('reset-search')
      this.ssrsUrl = null
    }
  }
}
</script>

<style scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.green-bg {
  background-color: #4CAF5033;
  color: #4CAF50;
}

.red-bg {
  background-color: #DB111133;
  color: #DB1111;
}

.custom-carousel-popup /deep/ .vs-popup {
  padding: 0 !important;
}
.carousel-header {
  padding: 24px 24px 0 24px;
}
.carousel-close-btn {
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
}
.main-carousel-img {
  width: 500px;
  height: 300px;
  object-fit: cover;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  z-index: 2;
}
.carousel-arrow.left {
  left: -5px;
}
.carousel-arrow.right {
  right: -5px;
}
.carousel-thumb-wrapper {
  border: 3px solid transparent;
  border-radius: 10px;
  padding: 2px;
  transition: border 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  cursor: pointer;
}
.carousel-thumb-wrapper.selected {
  border: 3px solid #FF7F57;
}
.carousel-thumb {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
}
</style>
